/**
 * Cross-tab activity sync via BroadcastChannel.
 * Falls back to localStorage events if BroadcastChannel is unavailable.
 */

export type SessionMessage =
  | { type: "active"; ts: number }
  | { type: "signout"; reason: string }
  | { type: "heartbeat-ack"; secondsUntilIdle: number; secondsUntilAbsolute: number };

type MessageHandler = (msg: SessionMessage) => void;

const CHANNEL_NAME = "resumeai-activity";
const LS_KEY = "resumeai-activity-sync";

let channel: BroadcastChannel | null = null;
const handlers: MessageHandler[] = [];

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (e) => {
      for (const h of handlers) h(e.data as SessionMessage);
    };
  }
  return channel;
}

// localStorage polyfill for environments without BroadcastChannel
function setupLocalStorageFallback() {
  window.addEventListener("storage", (e) => {
    if (e.key === LS_KEY && e.newValue) {
      try {
        const msg = JSON.parse(e.newValue) as SessionMessage;
        for (const h of handlers) h(msg);
      } catch {
        // swallow malformed localStorage values
      }
    }
  });
}

let fallbackSetup = false;

export function subscribe(handler: MessageHandler): () => void {
  handlers.push(handler);
  const ch = getChannel();
  if (!ch && !fallbackSetup) {
    setupLocalStorageFallback();
    fallbackSetup = true;
  }
  return () => {
    const idx = handlers.indexOf(handler);
    if (idx !== -1) handlers.splice(idx, 1);
  };
}

export function broadcast(msg: SessionMessage): void {
  const ch = getChannel();
  if (ch) {
    ch.postMessage(msg);
  } else {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ ...msg, _t: Date.now() }));
    } catch {
      // swallow storage quota errors
    }
  }
}

export function closeBroadcastChannel(): void {
  channel?.close();
  channel = null;
}
